/**
 * Speech service facade.
 *
 * One entry point that hides which STT vendor is active. The component just
 * asks for a SpeechProvider and the facade picks the right adapter based on
 * the value of the platform's `stt_provider` setting (set by super admins in
 * the Services page).
 *
 * Failure handling: if the requested provider is unavailable or its adapter
 * returns null (e.g. Sarvam during its stub phase), we silently fall back to
 * Azure so the candidate's interview is never broken by a misconfigured
 * setting. A console warning is logged for the developer.
 *
 * Phase 2A: this file exists and is wired to both adapters, but
 * QuestionSection.jsx doesn't import it yet. Phase 2B migrates the call sites.
 */

import { createAzureProvider } from './azureAdapter';
import { createSarvamProvider } from './sarvamAdapter';

/** Allowed provider identifiers — must mirror backend ALLOWED_STT_PROVIDERS. */
export const SUPPORTED_PROVIDERS = ['azure', 'sarvam'];

/** Returned when no provider was specified or an invalid one was. */
export const DEFAULT_PROVIDER = 'azure';

/**
 * Fetch the currently-active provider name from the backend.
 * Always resolves to a usable provider — falls back to DEFAULT_PROVIDER on
 * any error so the interview can always start.
 *
 * @returns {Promise<string>}
 */
export async function fetchActiveProviderName() {
    try {
        const res = await fetch('/api/settings/stt-provider');
        if (!res.ok) {
            console.warn(`[speechService] GET /api/settings/stt-provider returned ${res.status}, defaulting to "${DEFAULT_PROVIDER}"`);
            return DEFAULT_PROVIDER;
        }
        const data = await res.json();
        const value = (data && data.value) || DEFAULT_PROVIDER;
        if (!SUPPORTED_PROVIDERS.includes(value)) {
            console.warn(`[speechService] Unknown provider "${value}" from backend, defaulting to "${DEFAULT_PROVIDER}"`);
            return DEFAULT_PROVIDER;
        }
        return value;
    } catch (e) {
        console.warn('[speechService] Failed to fetch stt-provider setting, defaulting to azure:', e);
        return DEFAULT_PROVIDER;
    }
}

/**
 * Build a SpeechProvider instance for the given provider name.
 *
 * If the requested provider is unavailable (e.g. Sarvam stub returns null),
 * we transparently fall back to Azure so the interview keeps working.
 *
 * @param {string} providerName - "azure" | "sarvam"
 * @param {import('./types').SpeechProviderDeps} deps
 * @returns {import('./types').SpeechProvider}
 */
export function createSpeechProvider(providerName, deps) {
    if (providerName === 'sarvam') {
        const sarvam = createSarvamProvider(deps);
        if (sarvam) {
            console.log('[speechService] Using Sarvam STT adapter');
            return sarvam;
        }
        console.warn('[speechService] Sarvam adapter unavailable — falling back to Azure');
        return createAzureProvider(deps);
    }

    // Default + explicit "azure"
    if (providerName !== 'azure') {
        console.warn(`[speechService] Unknown provider "${providerName}" — falling back to Azure`);
    } else {
        console.log('[speechService] Using Azure STT adapter');
    }
    return createAzureProvider(deps);
}
